import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Tone = 'default' | 'accent' | 'win' | 'loss' | 'warn' | 'info';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: Tone;
  interactive?: boolean;
  padded?: boolean;
}

const TONE: Record<Tone, string> = {
  default:
    'bg-surface-elevated border-surface-border',
  accent:
    'bg-gradient-to-br from-accent-500/10 via-surface-elevated to-surface-elevated border-accent-500/30',
  win:
    'bg-win-DEFAULT/5 border-win-DEFAULT/25',
  loss:
    'bg-loss-DEFAULT/5 border-loss-DEFAULT/25',
  warn:
    'bg-warn-DEFAULT/5 border-warn-DEFAULT/25',
  info:
    'bg-info-DEFAULT/5 border-info-DEFAULT/25',
};

export function Card({
  tone = 'default',
  interactive = false,
  padded = true,
  className,
  children,
  ...rest
}: CardProps): ReactElement {
  return (
    <div
      {...rest}
      className={cn(
        'rounded-2xl border',
        TONE[tone],
        padded && 'p-5',
        interactive && 'transition-all duration-200 hover:border-surface-strong hover:shadow-lift cursor-pointer',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface StatProps {
  label: string;
  value: string | number;
  hint?: ReactNode;
  tone?: 'win' | 'loss' | 'neutral' | 'accent';
  mono?: boolean;
  trend?: 'up' | 'down' | null;
}

export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
  mono = true,
  trend,
}: StatProps): ReactElement {
  const valueClass =
    tone === 'win'
      ? 'text-win-DEFAULT'
      : tone === 'loss'
      ? 'text-loss-DEFAULT'
      : tone === 'accent'
      ? 'text-accent-500'
      : 'text-ink';
  return (
    <Card padded={false} className="p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-ink-subtle font-semibold">
          {label}
        </div>
        {trend && (
          <span
            className={cn(
              'inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold',
              trend === 'up'
                ? 'bg-win-DEFAULT/15 text-win-DEFAULT'
                : 'bg-loss-DEFAULT/15 text-loss-DEFAULT',
            )}
          >
            {trend === 'up' ? '▲' : '▼'}
          </span>
        )}
      </div>
      <div className={cn('mt-2 text-2xl font-bold leading-tight', valueClass, mono && 'font-mono')}>
        {value}
      </div>
      {hint && <div className="text-[11px] text-ink-faint mt-1 leading-snug">{hint}</div>}
    </Card>
  );
}

interface SectionProps {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Section({ title, action, children, className }: SectionProps): ReactElement {
  return (
    <section className={cn('space-y-3', className)}>
      {(title || action) && (
        <header className="flex items-end justify-between gap-3">
          {title && (
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-subtle font-semibold">
              {title}
            </div>
          )}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}
